import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ActivityOverTimeChart = ({ data, timeRange, isLoading }) => {
  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          Loading activity chart data...
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          No activity data available
        </div>
      </div>
    );
  }

  // Transform data for chart with proper date labels
  const chartData = data.map(item => {
    const date = new Date(item.bucket);
    let label;

    switch (timeRange) {
      case 'daily':
        // Show hour: "14:00", "15:00"
        label = date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
        break;
      case 'weekly':
        // Show day: "Jan 1", "Jan 2"
        label = date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
        break;
      case 'monthly':
        // Show day: "Jan 1", "Jan 2"
        label = date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
        break;
      case 'yearly':
        // Show month: "Jan 2024", "Feb 2024"
        label = date.toLocaleDateString('en-US', { 
          month: 'short', 
          year: 'numeric' 
        });
        break;
      default:
        label = date.toLocaleDateString('en-US', { 
          month: 'short', 
          year: 'numeric' 
        });
    }

    return {
      label,
      sessions: item.sessions || 0,
      web3Users: item.web3Users || 0,
      walletsConnected: item.walletsConnected || 0
    };
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border shadow-lg" style={{ borderColor: styles.borderColor, fontFamily: 'Raleway, sans-serif' }}>
          <p className="font-medium mb-2" style={{ color: styles.textPrimary }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={chartData}
        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
      >
        <CartesianGrid 
          strokeDasharray="3 3" 
          stroke="#E5E7EB"
          vertical={true}
          horizontal={true}
        />
        <XAxis 
          dataKey="label"
          tick={{ 
            fill: styles.textPrimary, 
            fontSize: 11,
            fontFamily: 'Raleway, sans-serif'
          }}
          axisLine={{ stroke: styles.borderColor }}
        />
        <YAxis 
          tick={{ 
            fill: styles.textPrimary, 
            fontSize: 11,
            fontFamily: 'Raleway, sans-serif'
          }}
          axisLine={{ stroke: styles.borderColor }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          wrapperStyle={{ fontFamily: 'Raleway, sans-serif', fontSize: '12px' }}
        />
        {/* Sessions - Light purple line */}
        <Line
          type="monotone"
          dataKey="sessions"
          stroke="#8b7bb6"
          strokeWidth={2}
          name="Sessions"
          isAnimationActive={true}
          dot={false}
        />
        {/* Web3 Users - Medium purple line */}
        <Line
          type="monotone"
          dataKey="web3Users"
          stroke="#5d4d86"
          strokeWidth={2}
          name="Web3 Users"
          isAnimationActive={true}
          dot={false}
        />
        {/* Wallets Connected - Darkest purple line */}
        <Line
          type="monotone"
          dataKey="walletsConnected"
          stroke="#1d0c46"
          strokeWidth={2}
          name="Wallet Connected"
          isAnimationActive={true}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default ActivityOverTimeChart;
